import { FilterViewType } from "@smeetz/ui-components";
import type { SearchTableConfig } from "@smeetz/ui-components";

/**
 * Build the SearchTableConfig consumed by `<SSearchTable>` in the customers
 * modal. Visible columns mirror the legacy `<el-table>` (email / full name /
 * country / city / zip / phone). All other ES-fetched fields stay as
 * `internalUsage` because the row-level "Select" / "Edit" actions copy them
 * into Vuex via `setSelectedCustomers` / `setEditedCustomers`.
 *
 * @returns {SearchTableConfig} Config ready to pass to SSearchTable.
 */
export function buildCustomersSearchTableConfig(): SearchTableConfig {
  return {
    id: "pos.customers",
    collection: "customer",
    searchFields: ["full_name", "first_name", "last_name", "email"],
    itemsLabelKey: "customers.title",
    selectable: false,
    pageSize: 10,
    pageSizeOptions: [10, 25, 50, 100],

    tabs: [
      {
        id: "all",
        name: "customers.title",
        type: FilterViewType.Everyone,
        default: true,
        position: 1.01,
        filters: [[]],
      },
    ],

    columns: {
      email: {
        localeKey: "columns.email-address",
        show: true,
        position: 1,
        "min-width": "230",
        type: "select-autocomplete",
        autocomplete: {
          engineKey: "customer",
          modelKeyLabel: "email",
          modelKeyValue: "email",
          searchFields: ["email"],
          groupBy: "email",
        },
        cell: { type: "default", prop: "email", sortable: true },
      },
      full_name: {
        localeKey: "columns.full-name",
        show: true,
        position: 2,
        "min-width": "260",
        type: "select-autocomplete",
        autocomplete: {
          engineKey: "customer",
          modelKeyLabel: "fullName",
          modelKeyValue: "fullName",
          searchFields: ["full_name"],
          groupBy: "full_name",
        },
        cell: { type: "default", prop: "fullName", sortable: true },
      },
      country: {
        localeKey: "customer.country",
        show: true,
        position: 3,
        "min-width": "180",
        type: "select-autocomplete",
        autocomplete: {
          engineKey: "customer",
          modelKeyLabel: "countryString",
          modelKeyValue: "country",
          searchFields: ["country"],
          groupBy: "country",
        },
        cell: { type: "default", prop: "countryString", sortable: true },
      },
      city: {
        localeKey: "customer.city",
        show: true,
        position: 4,
        "min-width": "180",
        type: "select-autocomplete",
        autocomplete: {
          engineKey: "customer",
          modelKeyLabel: "city",
          modelKeyValue: "city",
          searchFields: ["city"],
          groupBy: "city",
        },
        cell: { type: "default", prop: "city", sortable: true },
      },
      zip_code: {
        localeKey: "field.zip-code",
        show: true,
        position: 5,
        "min-width": "180",
        type: "select-autocomplete",
        autocomplete: {
          engineKey: "customer",
          modelKeyLabel: "zipCode",
          modelKeyValue: "zipCode",
          searchFields: ["zip_code"],
          groupBy: "zip_code",
        },
        cell: { type: "default", prop: "zipCode", sortable: true },
      },
      phone: {
        localeKey: "columns.phone-number",
        show: true,
        position: 6,
        "min-width": "180",
        type: "select-autocomplete",
        autocomplete: {
          engineKey: "customer",
          modelKeyLabel: "phone",
          modelKeyValue: "phone",
          searchFields: ["phone"],
          groupBy: "phone",
        },
        cell: { type: "default", prop: "phone", sortable: true },
      },

      actions: {
        localeKey: "cells.actions",
        show: true,
        readOnly: true,
        position: 99,
        "min-width": "180",
        pinned: "right",
        cell: { type: "default", prop: "id" },
      },

      id: { internalUsage: true },
      first_name: { internalUsage: true },
      last_name: { internalUsage: true },
      group_access: { internalUsage: true },
      birth_date: { internalUsage: true },
      email_marketing_organiser: { internalUsage: true },
      title: { internalUsage: true },
      allergies: { internalUsage: true },
      address: { internalUsage: true },
      language: { internalUsage: true },
      company: { internalUsage: true },
    },
  };
}
